import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { t } from '@aquilixshop/i18n';
import { resolveProductFromRef } from '../../sections/_shared/product.js';
import { sanitizeRichtextHtml } from '../../sanitize/sanitizeRichtextHtml.js';
import { marginFields, visibilityField } from '../_shared/blockFields.js';
import { marginStyle, num, str, visibilityCss } from '../_shared/blockRender.js';
/**
 * `offer-card` — a bordered mini-card for the buy-box Details column (top-tier Shopify PDP anatomy:
 * gift-with-purchase / extended-warranty / promo cards).
 *
 * TWO authoring modes (v1):
 *  - LINKED PRODUCT (feature 0023 pickers): pick a `product` and the card fills its **cover image, name,
 *    price and REAL availability** from the catalog and LINKS to the product page — a gift-with-purchase
 *    is a real product, not a hand-typed image + chip. Availability drives a grey "sold out" pill from
 *    live inventory. In the builder (refs not resolved) it previews the demo product; on the live store a
 *    picked product that is draft/deleted HIDES the card (never shows the demo to a customer).
 *  - CUSTOM (no product): the generic static card — an uploaded image, a manual title + status chip — so
 *    a warranty / promo card (which is NOT a product) still works.
 *
 * The section label, body richtext and fine print are ALWAYS seller copy (the offer's terms). Not gated on
 * the product context — it renders in any container it is dropped into. NO cart logic in v1 (a real gift
 * auto-added to cart is a separate promo feature).
 */
export const offerCardBlockSchema = {
    type: 'offer-card',
    name: 'Offer card',
    fields: [
        visibilityField,
        // ── Content ────────────────────────────────────────────────────────────────────────────────────
        {
            id: 'product',
            type: 'product-picker',
            label: 'Linked product',
            group: 'Content',
            info: 'Link a product — its image, name, price and live availability fill the card and it links to the product page. Leave empty for a fully custom card.',
        },
        {
            id: 'sectionLabel',
            type: 'text',
            label: 'Section label',
            group: 'Content',
            info: 'Small heading shown above the card (e.g. "Cadeau offert"). Empty = hidden.',
            default: '',
            maxLength: 60,
        },
        {
            id: 'image',
            type: 'image',
            label: 'Image',
            group: 'Content',
            info: 'Used only when no product is linked (a linked product uses its own cover image).',
        },
        {
            id: 'imageSize',
            type: 'range',
            label: 'Image size',
            group: 'Content',
            min: 40,
            max: 120,
            step: 4,
            default: 64,
            unit: 'px',
        },
        {
            id: 'title',
            type: 'text',
            label: 'Title',
            group: 'Content',
            info: 'Used only when no product is linked (a linked product uses its own name).',
            default: 'Votre offre',
            defaultKey: 'section.your_offer',
            maxLength: 80,
        },
        {
            id: 'chipText',
            type: 'text',
            label: 'Status chip',
            group: 'Content',
            info: 'Small status pill next to the title (e.g. "Offert"). A linked product that is out of stock forces a grey "sold out" pill. Empty = hidden.',
            default: '',
            maxLength: 30,
        },
        {
            id: 'chipTone',
            type: 'select',
            label: 'Chip tone',
            group: 'Content',
            default: 'neutral',
            options: [
                { value: 'neutral', label: 'Neutral' },
                { value: 'success', label: 'Success (green)' },
                { value: 'warning', label: 'Warning (amber)' },
                { value: 'sold-out', label: 'Sold out (grey)' },
            ],
            visibleWhen: { field: 'chipText', notEquals: '' },
        },
        {
            id: 'soldOutLabel',
            type: 'text',
            label: '"Sold out" label',
            group: 'Content',
            info: 'Pill text when a LINKED product is out of stock.',
            default: 'Épuisé',
            defaultKey: 'product.sold_out_short',
            maxLength: 30,
        },
        {
            id: 'body',
            type: 'richtext',
            label: 'Body',
            group: 'Content',
            default: {
                format: 'html',
                html: '<p>Décrivez votre offre (cadeau, garantie, promotion…).</p>',
            },
        },
        {
            id: 'finePrint',
            type: 'text',
            label: 'Fine print',
            group: 'Content',
            info: 'Small muted line under the body (conditions, limits). Empty = hidden.',
            default: '',
            maxLength: 120,
        },
        // ── Styling ────────────────────────────────────────────────────────────────────────────────────
        {
            id: 'cornerRadius',
            type: 'range',
            label: 'Corner radius',
            group: 'Styling',
            min: 0,
            max: 32,
            step: 2,
            default: 12,
            unit: 'px',
        },
        ...marginFields(15, 15),
    ],
};
/** Chip tone → { background, color }. Fixed readable pairs (no theme token exists for status colors). */
const CHIP_TONES = {
    neutral: { bg: 'var(--aqx-scheme-surface)', fg: 'var(--aqx-scheme-text)' },
    success: { bg: '#e7f5ec', fg: '#157347' },
    warning: { bg: '#fdf3e0', fg: '#a05a00' },
    'sold-out': { bg: '#ececec', fg: '#767676' },
};
/** The linked product's cover image (the first image), or undefined when it ships none. */
function coverOf(product) {
    const img = product.images?.[0];
    return img ? { url: img.url, alt: img.alt ?? product.name } : undefined;
}
const OfferCardBlock = ({ block, context }) => {
    const bs = block.settings;
    const cls = `aqx-oc-${block.id}`;
    // ── Optional LINKED product (feature 0023 pickers) ───────────────────────────────────────────────
    const productRef = bs.product;
    let linkedProduct = null;
    let productHref = '';
    let soldOut = false;
    if (productRef?.id) {
        const resolved = resolveProductFromRef(context, productRef);
        // Live store + the picked product is draft/deleted → hide (never render the demo to a real customer).
        if (resolved.hidden)
            return null;
        linkedProduct = resolved.product;
        productHref = context.resolveUrl({ type: 'product', slug: linkedProduct.slug });
        // Sold-out signal matches CardProduct across the theme: a non-active product is unavailable.
        soldOut = linkedProduct.status !== 'active';
    }
    const sectionLabel = str(bs.sectionLabel).trim();
    // Image: the linked product's cover wins; else the manual upload.
    const manualImage = bs.image;
    const cover = linkedProduct ? coverOf(linkedProduct) : undefined;
    const imageUrl = linkedProduct
        ? (cover?.url ?? '')
        : typeof manualImage?.url === 'string'
            ? manualImage.url
            : '';
    const imageAlt = linkedProduct ? (cover?.alt ?? linkedProduct.name) : (manualImage?.alt ?? '');
    const imageSize = num(bs.imageSize, 64);
    // Title + price: the linked product's name/price win; a custom card keeps the manual title (no price).
    const title = linkedProduct ? linkedProduct.name : str(bs.title, 'Votre offre');
    const priceText = linkedProduct ? context.formatMoney(linkedProduct.price) : '';
    // Chip: a linked-but-out-of-stock product forces a grey "sold out" pill; else the seller's manual chip.
    const manualChip = str(bs.chipText).trim();
    const chipText = soldOut
        ? manualChip || str(bs.soldOutLabel, t(context, 'product.sold_out_short'))
        : manualChip;
    const toneKey = soldOut ? 'sold-out' : str(bs.chipTone, 'neutral');
    const tone = CHIP_TONES[toneKey] ?? CHIP_TONES.neutral;
    const rt = bs.body;
    // ABSENT setting → the schema default (house convention); an explicit empty richtext stays empty.
    const bodyHtml = sanitizeRichtextHtml(rt === undefined
        ? '<p>Décrivez votre offre (cadeau, garantie, promotion…).</p>'
        : rt?.format === 'html'
            ? rt.html
            : '');
    const finePrint = str(bs.finePrint).trim();
    const radius = num(bs.cornerRadius, 12);
    const vis = visibilityCss(cls, bs.visibility);
    const css = `.${cls}{font-family:var(--aqx-theme-font)}` +
        // The linked title is a link — inherit the title colour, underline only on hover/focus.
        `.${cls}__title{color:var(--aqx-scheme-text);text-decoration:none}` +
        `a.${cls}__title:hover,a.${cls}__title:focus-visible{text-decoration:underline}` +
        `.${cls}__body p{margin:0 0 4px;font-size:var(--aqx-theme-text-sm, 14px);` +
        `line-height:var(--aqx-theme-lh-snug, 1.45);color:var(--aqx-scheme-text)}` +
        `.${cls}__body p:last-child{margin-bottom:0}` +
        vis;
    const cardStyle = {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        border: '1px solid var(--aqx-scheme-border)',
        borderRadius: `${radius}px`,
        padding: '12px',
        background: 'var(--aqx-scheme-bg)',
    };
    const titleStyle = {
        fontSize: 'var(--aqx-theme-text-base, 16px)',
        fontWeight: 600,
        lineHeight: 1.3,
    };
    const imgEl = imageUrl ? (_jsx("img", { src: imageUrl, alt: imageAlt, loading: "lazy", style: {
            inlineSize: `${imageSize}px`,
            blockSize: `${imageSize}px`,
            objectFit: 'contain',
            borderRadius: `${Math.max(0, radius - 4)}px`,
            background: 'var(--aqx-scheme-surface)',
            display: 'block',
        } })) : null;
    // Wrap a node in the product link when a product is linked (image + title only — never the body, so a
    // richtext link can't nest inside an anchor).
    const linkWrap = (node, extraClass, style) => productHref ? (_jsx("a", { href: productHref, className: extraClass, style: style, children: node })) : (node);
    return (_jsxs(_Fragment, { children: [_jsx("style", { dangerouslySetInnerHTML: { __html: css } }), _jsxs("div", { className: cls, style: marginStyle(bs, 15, 15), children: [sectionLabel ? (_jsx("div", { className: `${cls}__label`, style: {
                            fontSize: 'var(--aqx-theme-text-sm, 14px)',
                            fontWeight: 600,
                            color: 'var(--aqx-scheme-text)',
                            marginBottom: '8px',
                        }, children: sectionLabel })) : null, _jsxs("div", { className: `${cls}__card`, style: cardStyle, children: [imgEl ? _jsx("span", { style: { flexShrink: 0, lineHeight: 0 }, children: linkWrap(imgEl) }) : null, _jsxs("div", { style: { minWidth: 0, flex: '1 1 auto' }, children: [_jsxs("div", { style: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            gap: '8px',
                                            marginBottom: '4px',
                                        }, children: [title ? (productHref ? (_jsx("a", { href: productHref, className: `${cls}__title`, style: titleStyle, children: title })) : (_jsx("span", { className: `${cls}__title`, style: titleStyle, children: title }))) : null, chipText ? (_jsx("span", { className: `${cls}__chip`, style: {
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    padding: '2px 10px',
                                                    borderRadius: '999px',
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    lineHeight: 1.5,
                                                    whiteSpace: 'nowrap',
                                                    background: tone.bg,
                                                    color: tone.fg,
                                                }, children: chipText })) : null] }), priceText ? (_jsx("div", { className: `${cls}__price`, style: {
                                            fontSize: 'var(--aqx-theme-text-sm, 14px)',
                                            fontWeight: 600,
                                            color: 'var(--aqx-scheme-text)',
                                            marginBottom: '4px',
                                        }, children: priceText })) : null, bodyHtml ? (_jsx("div", { className: `${cls}__body`, dangerouslySetInnerHTML: { __html: bodyHtml } })) : null, finePrint ? (_jsx("div", { className: `${cls}__fine`, style: {
                                            marginTop: '6px',
                                            fontSize: '12px',
                                            color: 'var(--aqx-scheme-muted)',
                                            lineHeight: 1.4,
                                        }, children: finePrint })) : null] })] })] })] }));
};
export const offerCardBlock = {
    type: 'offer-card',
    schema: offerCardBlockSchema,
    Component: OfferCardBlock,
    meta: { label: 'Offer card' },
};
//# sourceMappingURL=index.js.map